import AttachmentIcon from "@mui/icons-material/Attachment";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { usePostHog } from "posthog-js/react";

import { ErrorTypography } from "components/ErrorTypography";
import { LoadingWrappedNode } from "components/LoadingWrappedNode";
import { ClientAppEvent } from "constants/index";
import { useRoleContext } from "hooks";
import { useShipmentDetailFiles } from "hooks/useShipmentDetailFiles";
import { bytesToMb } from "utils";

interface Props {
  shipmentId: number | undefined;
}

export function SupplierShipmentDetailFiles(props: Props) {
  const { shipmentId } = props;
  const { role } = useRoleContext();
  const posthog = usePostHog();

  const { loading, error, files } = useShipmentDetailFiles(shipmentId);

  return (
    <Stack>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="h6">Attachments</Typography>
        <AttachmentIcon />
      </Stack>
      <LoadingWrappedNode loading={loading}>
        {error && <ErrorTypography error={error} />}

        {!error && files.length === 0 && !loading && (
          <Typography
            variant="body2"
            color="text.secondary"
            mt=".5rem"
            style={{ fontStyle: "italic" }}
          >
            No files included for this shipment
          </Typography>
        )}

        {!error && files.length > 0 && (
          <List
            sx={{
              width: "100%",
              position: "relative",
              overflow: "auto",
              maxHeight: 300,
              "& ul": { padding: 0 },
            }}
          >
            {files.map((file) => (
              <ListItem key={`${file.filename}-${file.size_bytes}-${file.url}`}>
                <ListItemButton
                  component="a"
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => {
                    posthog?.capture(
                      ClientAppEvent.SHIPMENT_DETAIL_FILE_DOWNLOADED,
                      {
                        shipment_id: shipmentId,
                        filename: file.filename,
                        size_bytes: file.size_bytes,
                        triggered_by_role: role?.id,
                        role_type: role?.type,
                        is_supplier_shipment: true,
                      },
                    );
                  }}
                >
                  <ListItemText
                    primary={file.filename}
                    secondary={`Size: ${bytesToMb(file.size_bytes)} MB`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </LoadingWrappedNode>
    </Stack>
  );
}
